import { Box } from '@mui/material'
import { portalLanguagesData } from 'app/appSettings'
import parse, { domToReact } from 'html-react-parser'
import { Link } from 'react-router-dom'
import rehypeStringify from 'rehype-stringify'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { myI18n } from 'translation/I18nConnectedProvider'
import { unified } from 'unified'
import { checkIfnoRecords } from 'utils'
import { containsMarkdown, isFalsy } from 'utilsTS'

export const markdownToHtmlProcessor = unified()
  .use(remarkParse)
  .use(remarkRehype, { allowDangerousHtml: true })
  .use(rehypeStringify, { allowDangerousHtml: true })

/**
 * Given locale key, returns correct label for translation data object, which is used in multilingual portal features.
 * @function
 * @category Translations
 * @param {string} langVersion Locale key.
 * @param {object} data Data object cotaining labels under locale keys.
 * @param {any} i18n I18n object used for translating connected Portal Translations. Defaults to i18n instance used in a project.
 * @param {string} [fallbackKey='en'] Locale key that will be used if label for given language key will be empty.
 * @return {string} Returns correct label from translation data object.
 */
export const getLabelFromTranslationData = (props: {
  /**
   * Data object cotaining labels under locale keys.
   */
  data
  /**
   * Locale key
   */
  langVersion: string
  /**
   * I18n object used for translating connected Portal Translations. Defaults to i18n instance used in a project.
   */
  i18n?
  /**
   * Locale key that will be used if label for given language key will be empty.
   */
  fallbackKey?
}): string => {
  const { data, langVersion, i18n = myI18n, fallbackKey = 'en' } = props
  if (data == null) {
    return ''
  }
  let toRet = !isFalsy(data.portalTranslationId)
    ? i18n._(data.portalTranslationId)
    : data[langVersion]
  if (isFalsy(toRet) && !isFalsy(data[fallbackKey])) {
    toRet = data[fallbackKey]
  }
  return toRet ?? ''
}

/**
 * Returns the localized label from a `Survey_Text__c` Salesforce object.
 *
 * @param userLang - Locale key.
 * @param titleTranslation - `Survey_Text__c` object instance.
 * @param fieldName - Field from the `Survey_Text__c` object that contains the translation. Defaults to `'Text'`.
 * @param parsed - Determines whether to read from parsed data shape used by us in the project (`surveyTexts`) instead of Salesforce relation shape (`Survey_Texts__r`). Defaults to `false`.
 * @param fallbackKey - Locale key used when no label exists for the target language. Defaults to `'en-ca'`.
 * @returns The localized label, or parsed Markdown content if the resolved text contains Markdown.
 */
export function getTranslationFromTextStructure (
  {
    userLang,
    titleTranslation,
    fieldName = 'Text',
    parsed = false,
    fallbackKey = 'en-ca',
    returnString = false
  }: {
    userLang: string
    titleTranslation: any
    fieldName?: string
    parsed?: boolean
    fallbackKey?: string
    returnString?: boolean
  }
): string | JSX.Element {
  let ret: string | JSX.Element = ''

  if (checkIfnoRecords(titleTranslation, parsed)) {
    return ret
  }
  const langData = portalLanguagesData[userLang]
  if (langData == null) {
    return ret
  }

  const surveyTextsKey = parsed ? 'surveyTexts' : 'Survey_Texts__r'

  titleTranslation[surveyTextsKey].records.some((trans) => {
    if (trans.Language__c === langData.surveyTextKey) {
      ret = trans.Text__c
      if (
        trans[fieldName + '_Is_Portal_Translation_Id__c'] != null &&
        typeof ret === 'string'
      ) {
        ret = myI18n._(ret)
      }
      return true
    }
    return false
  })

  if (ret == null && fallbackKey != null) {
    titleTranslation[surveyTextsKey].records.some((trans) => {
      if (trans.Language__c === fallbackKey) {
        ret = trans.Text__c
        if (
          trans[fieldName + '_Is_Portal_Translation_Id__c'] != null &&
          typeof ret === 'string'
        ) {
          ret = myI18n._(ret)
        }
        return true
      }
      return false
    })
  }

  if (containsMarkdown(ret) && !returnString) {
    ret = (
      <Box
        sx={{
          '& a': {
            color: 'secondary.green',
            textDecoration: 'underline'
          },
          '& a:hover': {
            color: 'primary.main'
          }
        }}
      >{parse(markdownToHtmlProcessor.processSync(ret).toString(), {
        replace (domNode: any) {
          if (domNode.type === 'tag' && domNode.name === 'a') {
            const href = domNode.attribs?.href as string
            if (href?.startsWith('/')) {
              return <Link to={href}>{domToReact(domNode.children)}</Link>
            }
            return (
              <a {...domNode.attribs} target='_blank' rel='noopener noreferrer'>
                {domToReact(domNode.children)}
              </a>
            )
          }
        }
      })}
      </Box>
    )
  }

  return ret
}

/**
 * Parses salesforce api value to string matching portal translation id.
 * @function
 * @category Translations
 * @param {string} apiValue Salesforce api value string.
 * @param {string} [prefix=''] String that will be added to the begining of returned string.
 * @returns {string} String matching portal translation id.
 */
export function parseSFApiValueToTranslationId (apiValue, prefix = ''): string {
  const toRet = String(apiValue)
    .toUpperCase()
    .replaceAll(' - ', ' ')
    .replace(/\s+/g, '_')
  return (prefix != null && prefix !== '') ? `${prefix}_${toRet}` : toRet
}

export const getMomentDateFormatForLanguage = (lang: string): string => {
  if (lang === 'fr') {
    return 'DD/MM/YYYY'
  } else {
    return 'YYYY-MM-DD'
  }
}

export function getTranslationIdFromSalesforceApiValue (apiValue): string {
  const toRet = String(apiValue)
    .replaceAll(' - ', ' ')
    .replace(/\s+/g, '_')
  return `SF_API_${toRet}`
}
